// src/pages/BOLGenerator.js - FIXED DISPLAY ID ISSUE
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../services/firebase';
import { useAuth } from '../context/AuthContext';
import { generateProfessionalBOL } from '../services/bolService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import Modal from '../components/UI/Modal';

// Function to generate display ID (same as customer dashboard)
const generateDisplayId = (firebaseId, index) => {
  if (firebaseId && firebaseId.includes('_')) {
    const parts = firebaseId.split('_');
    if (parts.length >= 3) {
      const timestamp = parts[1];
      const randomPart = parts[2];
      return `SH${timestamp.slice(-6)}${randomPart.substring(0, 4).toUpperCase()}`;
    }
  }
  return `SH${String(index + 1).padStart(6, '0')}`;
};

const BOLGenerator = () => {
  const { shipmentId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isAdmin } = useAuth();
  
  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [signatureData, setSignatureData] = useState({
    pickupSignature: '',
    deliverySignature: '',
    pickupPhoto: null,
    deliveryPhoto: null
  });

  useEffect(() => {
    if (!isAdmin) {
      setMessage({ 
        type: 'error', 
        text: 'Admin access required to view BOL documents.' 
      });
      setLoading(false);
      return;
    }

    loadShipment();
  }, [shipmentId, isAdmin]);

  // ✅ FIXED: Load shipment with displayId
  const loadShipment = async () => {
    try {
      const shipmentDoc = await getDoc(doc(db, 'shipments', shipmentId));
      if (shipmentDoc.exists()) {
        // ✅ Generate displayId for the shipment
        const displayId = generateDisplayId(shipmentDoc.id, 0);
        const shipmentData = { 
          id: shipmentDoc.id, 
          ...shipmentDoc.data(),
          displayId: displayId // ✅ Ensure displayId is included
        };
        setShipment(shipmentData);
        
        // Load existing signature data if available
        if (shipmentData.signatures) {
          setSignatureData(shipmentData.signatures);
        }
        
        console.log('Loaded shipment with displayId:', {
          id: shipmentData.id,
          displayId: shipmentData.displayId,
          invoiceNumber: shipmentData.invoiceNumber
        });
      } else {
        setMessage({ type: 'error', text: 'Shipment not found.' });
      }
    } catch (error) {
      console.error('Error loading shipment:', error);
      setMessage({ type: 'error', text: 'Error loading shipment details.' });
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureCapture = (type, signature) => {
    setSignatureData(prev => ({
      ...prev,
      [type]: signature
    }));
  };

  const handlePhotoCapture = async (type, file) => {
    try {
      // Upload photo to storage
      const photoRef = ref(storage, `bols/${shipmentId}/${type}_photo_${Date.now()}.jpg`);
      await uploadBytes(photoRef, file);
      const photoUrl = await getDownloadURL(photoRef);

      setSignatureData(prev => ({
        ...prev,
        [type]: photoUrl
      }));

      // Update shipment with photo URL
      await updateDoc(doc(db, 'shipments', shipmentId), {
        [`signatures.${type}`]: photoUrl,
        updatedAt: serverTimestamp()
      });

    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Error uploading photo. Please try again.');
    }
  };

  // ✅ FIXED: Generate BOL with displayId
  const handleGenerateBOL = async () => {
    if (!shipment || !isAdmin) return;

    setGenerating(true);
    setMessage({ type: '', text: '' });

    try {
      // Check payment verification for non-Stripe payments
      if (shipment.paymentMethod !== 'Stripe' && !shipment.paymentVerified) {
        alert('Please verify payment before generating BOL');
        return;
      }

      console.log('Starting professional BOL generation process...');
      console.log('Shipment data being passed to BOL generator:', {
        id: shipment.id,
        displayId: shipment.displayId, // ✅ Now includes displayId
        invoiceNumber: shipment.invoiceNumber,
        name: shipment.name
      });
      
      // ✅ FIXED: Use the imported service function with displayId
      const bolUrl = await generateProfessionalBOL(shipment, signatureData, currentUser);
      
      setMessage({ 
        type: 'success', 
        text: 'Professional BOL generated successfully!' 
      });
      
      // Reload shipment to get updated BOL URL
      await loadShipment();
      
    } catch (error) {
      console.error('Error generating BOL:', error);
      setMessage({ type: 'error', text: 'Error generating BOL: ' + error.message });
    } finally {
      setGenerating(false);
    }
  };

  const handlePreviewBOL = async () => {
    if (!shipment?.bolUrl || !isAdmin) return;

    try {
      setPreviewUrl(shipment.bolUrl);
      setIsPreviewOpen(true);
    } catch (error) {
      console.error('Error previewing BOL:', error);
      setMessage({ type: 'error', text: 'Error previewing BOL.' });
    }
  };

  const handleDownloadBOL = () => {
    if (!shipment?.bolUrl || !isAdmin) return;
    
    try {
      const link = document.createElement('a');
      link.href = shipment.bolUrl;
      link.download = `BOL-${shipment.invoiceNumber || shipment.id}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading BOL:', error);
      setMessage({ type: 'error', text: 'Error downloading BOL.' });
    }
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    return address.split('\n').map((line, index) => (
      <div key={index}>{line}</div>
    ));
  };

  // Signature Capture Component with notices
  const SignatureCapture = ({ type, title, notice }) => (
    <div className="border rounded-lg p-4 mb-4">
      <h4 className="font-semibold mb-2">{title}</h4>
      
      {/* Signature Notice */}
      {notice && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
          {notice}
        </div>
      )}
      
      <div className="flex space-x-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Signature
          </label>
          <input
            type="text"
            placeholder="Enter name for signature"
            value={signatureData[`${type}Signature`] || ''}
            onChange={(e) => handleSignatureCapture(`${type}Signature`, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Photo Upload
          </label>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => {
              if (e.target.files[0]) {
                handlePhotoCapture(`${type}Photo`, e.target.files[0]);
              }
            }}
            className="w-full"
          />
          {signatureData[`${type}Photo`] && (
            <img 
              src={signatureData[`${type}Photo`]} 
              alt={`${type} photo`}
              className="mt-2 h-20 object-cover rounded"
            />
          )}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading shipment details..." />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-4">Admin privileges required to access BOL Generator.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!shipment) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Shipment Not Found</h2>
          <button
            onClick={() => navigate('/admin')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Back to Admin Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin')}
            className="flex items-center text-blue-600 hover:text-blue-700 mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Admin Dashboard
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Professional Bill of Lading Generator</h1>
          <p className="text-gray-600 mt-2">Admin Access - Complete BOL Management with Signatures</p>
        </div>

        {/* Message Alert */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'error' 
              ? 'bg-red-100 border border-red-400 text-red-700' 
              : message.type === 'warning'
              ? 'bg-yellow-100 border border-yellow-400 text-yellow-700'
              : 'bg-green-100 border border-green-400 text-green-700'
          }`}>
            {message.text}
          </div>
        )}

        {/* Shipment Summary */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Shipment Summary</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Shipment Details</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-gray-600">Shipment ID</dt>
                  <dd className="text-sm text-gray-900 font-mono">{shipment.displayId || 'N/A'}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Invoice Number</dt>
                  <dd className="text-sm text-gray-900 font-mono">{shipment.invoiceNumber}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Item Description</dt>
                  <dd className="text-sm text-gray-900">{shipment.itemDescription}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Weight</dt>
                  <dd className="text-sm text-gray-900">{shipment.weight} lbs</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Agreed Price</dt>
                  <dd className="text-sm text-gray-900 font-semibold">${shipment.agreedPrice}</dd>
                </div>
              </dl>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Address Information</h3>
              <dl className="space-y-2">
                <div>
                  <dt className="text-sm font-medium text-gray-600">Pickup Address</dt>
                  <dd className="text-sm text-gray-900">{formatAddress(shipment.pickupAddress)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-600">Delivery Address</dt>
                  <dd className="text-sm text-gray-900">{formatAddress(shipment.dropoffAddress)}</dd>
                </div>
              </dl>
            </div>
          </div>
        </div>

        {/* Signature Capture Sections */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Signature & Photo Capture</h2>
          
          <SignatureCapture 
            type="pickup" 
            title="Driver Name & Signature (Pickup)" 
            notice="Carrier acknowledges receipt of packages and required placards. Carrier certifies emergency response information was made available and/or carrier has the DOT emergency response guidebook or equivalent documentation in the vehicle. Property described above is received in good order, except as noted."
          />
          
          <SignatureCapture 
            type="delivery" 
            title="Customer Name & Signature (Delivery)" 
            notice="This is to certify that the above named materials are properly classified, packaged, marked and labeled, and are in proper condition for transportation according to the applicable regulations of the DOT."
          />

          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Signatures and photos will be embedded in the professional BOL document. 
              Customers can only view the completed BOL after delivery when all signatures and photos are captured.
            </p>
          </div>
        </div>

        {/* BOL Actions */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">BOL Document Generation</h2>
          
          {!shipment.bolUrl ? (
            <div className="text-center py-8">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">No Professional BOL Generated</h3>
              <p className="mt-1 text-sm text-gray-500">Generate a professional Bill of Lading with signatures and photos.</p>
              <div className="mt-6">
                <button
                  onClick={handleGenerateBOL}
                  disabled={generating}
                  className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {generating ? (
                    <>
                      <LoadingSpinner size="small" text="" />
                      <span className="ml-2">Generating Professional BOL...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Generate Professional BOL
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <svg className="mx-auto h-12 w-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">Professional BOL Ready</h3>
              <p className="mt-1 text-sm text-gray-500">Your professional Bill of Lading has been generated with signatures and photos.</p>
              <div className="mt-6 flex justify-center space-x-4">
                <button
                  onClick={handlePreviewBOL}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Preview BOL
                </button>
                <button
                  onClick={handleDownloadBOL}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Download BOL
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* BOL Preview Modal */}
      <Modal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        title="Professional BOL Preview"
        size="full"
      >
        <div className="h-96 sm:h-120 flex flex-col">
          <div className="flex-1">
            <iframe
              src={previewUrl}
              className="w-full h-full border-0"
              title="Professional BOL Preview"
            />
          </div>
          <div className="flex justify-end pt-4 border-t border-gray-200 mt-4">
            <button
              onClick={handleDownloadBOL}
              className="mr-4 px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md"
            >
              Download BOL
            </button>
            <button
              onClick={() => setIsPreviewOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BOLGenerator;